<template>
  <div class="app-payment-wrapper">
    <slot name="txtBefore"></slot>
    <b-form-group>
      <b-form-radio v-if="creditCardPossible" v-model="_paymentType" name="paymentType" value="creditCard" :state="ccValidState">
        <slot name="lblCreditCard">Kreditkarte</slot>
      </b-form-radio>
      <PaymentCreditCard
        v-if="_paymentType === 'creditCard' && creditCardPossible"
        :redirect-url="creditCardRedirectUrl"
        :personal-data="paymentContext"
        :process-name="processName"
        @start="creditCardStart"
        :creditCardInformation.sync="creditCardInformation"
        :cc-frame-address="creditCardFrameAddress"
      >
        <template v-for="slot in possibleChildSlotsFor('PaymentCreditCard')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </PaymentCreditCard>
      <b-form-radio v-if="bankAccountPossible" v-model="_paymentType" name="paymentType" value="bankAccount">
        <slot name="lblBankAccount">Lastschrift</slot>
      </b-form-radio>
      <PaymentBankAccount v-if="_paymentType === 'bankAccount' && bankAccountPossible"
                          :iban.sync="bankAccount.iban"
                          :owner.sync="bankAccount.owner"
                          :paymentConfirmation.sync="bankAccount.paymentConfirmation"
                          :invalid.sync="bankAccountInvalid"
                          :customer="paymentContext"
      >
        <template v-for="slot in possibleChildSlotsFor('PaymentBankAccount')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </PaymentBankAccount>
    </b-form-group>
  </div>
</template>

<script>
import PaymentBankAccount from '@/components/process/generic/Payment/BankAccount.vue'
import PaymentCreditCard from '@/components/process/generic/Payment/CreditCard.vue'
import { mapGetters } from 'vuex'
import syncProperty from '@/mixins/syncProperty'
import { validationMixin } from 'vuelidate'
import preFilledInput from '@/mixins/preFilledInput'
import { required } from 'vuelidate/lib/validators'
import slotProxy from '@/mixins/slotProxy'

export default {
  name: 'PaymentWrapper',
  data () {
    return {
      paymentType: null,
      bankAccount: {
        iban: null,
        owner: null,
        paymentConfirmation: null
      },
      bankAccountInvalid: true
    }
  },
  mounted () {
    // wenn wir über einen kreditkarten-Callback aufgerufen wurden, müssen wir die CreditCard Komponente auswählen:
    const urlParameters = new URLSearchParams(window.location.search)
    if (urlParameters.has('id') && urlParameters.has('resourcePath')) {
      this.paymentType = 'creditCard'
    } else if (this.preSelectedPayment) {
      this.applyPreSelectedPayment()
    } else if (this.token !== null && this.ccStep === 'completed') {
      // wenn es ein credit Card token im store gibt, wählen wir Kreditkarte vor
      this.paymentType = 'creditCard'
    }
  },
  components: { PaymentCreditCard, PaymentBankAccount },
  computed: {
    ...mapGetters([
      'brand'
    ]),
    ...mapGetters('payment/creditCard', {
      token: 'token',
      ccStep: 'step',
      creditCard: 'creditCard'
    }),
    _paymentType: {
      get: function () {
        return this.paymentType || 'bankAccount'
      },
      set: function (value) {
        this.paymentType = value
      }
    },
    selectedPayment: function () {
      if (this.ccSelected) {
        return {
          paymentType: 'creditCard',
          ...this.creditCardInformation
        }
      }
      if (this.withOutPayment) {
        return {
          paymentType: 'withoutPayment'
        }
      }
      if (this.paypalSelected) {
        return {
          paymentType: 'paypal'
        }
      }
      return {
        paymentType: 'bankAccount',
        ...this.bankAccount
      }
    },
    ccSelected: function () {
      return this._paymentType === 'creditCard'
    },
    bankSelected: function () {
      return this._paymentType === 'bankAccount'
    },
    ccValidState: function () {
      if (this.ccStep !== 'completed') {
        return null
      }
      if (this.ccSelected === false) {
        return null
      }
      if (this.creditCardInformation === null) {
        return false
      }
      return this.creditCardInformation.registrationId !== null
    }
  },
  watch: {
    selectedPayment: {
      immediate: true,
      handler: function (value) {
        this.$emit('update:payment', value)
      }
    },
    '$v.$invalid': function (newValue) {
      this.$emit('update:paymentInvalid', newValue)
    }
  },
  methods: {
    creditCardStart () {
      this.creditCardInformation = null
    },
    applyPreSelectedPayment () {
      const payment = Object.assign({}, this.preSelectedPayment)
      this.paymentType = payment.paymentType
      delete payment.paymentType
      if (this.paymentType === 'creditCard') {
        this.creditCardInformation = payment
      } else if (this.paymentType === 'bankAccount') {
        this.bankAccount = payment
      }
    }
  },
  validations () {
    const paymentValidator = (params) => {
      if (this.withOutPayment) {
        return true
      }
      if (this.paypalSelected) {
        return true
      }
      if (params.ccSelected) {
        if (params.creditCardInformation === null) {
          return false
        }
        return params.creditCardInformation.registrationId !== null
      }
      if (params.bankSelected) {
        return params.bankAccountInvalid === false
      }
      return false
    }
    return {
      payment: paymentValidator
    }
  },
  props: {
    creditCardFrameAddress: {
      type: String
    },
    creditCardRedirectUrl: {
      type: String
    },
    paymentInvalid: {
      type: Boolean
    },
    paymentContext: {
      type: Object
    },
    bankAccountPossible: {
      type: Boolean,
      default: true
    },
    creditCardPossible: {
      type: Boolean,
      default: true
    },
    noPaymentPossible: {
      type: Boolean,
      default: true
    },
    processName: {
      type: String,
      required
    }
  },
  mixins: [
    validationMixin,
    slotProxy(),
    syncProperty('payment', 'preSelectedPayment'),
    preFilledInput('creditCardInformation', 'creditCard')
  ]
}
</script>
